import React from 'react';
import { StyleSheet, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
// eslint-disable-next-line import/no-extraneous-dependencies
import { uniqBy } from 'lodash';
import {
  BodyText,
  Button,
  HStack,
  IllusEmployeeBag,
  Image,
  LoadingPlaceholder,
  theme,
  useResponsive,
} from '@sweetgreen/garnish';

import {
  AccountScreenContainer,
  AccountScreenSection,
  AccountScreenTitle,
  SharedLineItem,
} from '@order/components';
import type { PartialLineItem } from '@order/graphql';
import { useLocalizationContext } from '@order/Localization';

import { useReorderLineItem } from '../ReorderingScreen/hooks';
import type { FavoritesQuery } from './GraphQL/Favorites.generated';
import { useFavoritesQuery } from './GraphQL/Favorites.generated';

export const FavoritesScreen = (): React.ReactElement => {
  const { t } = useLocalizationContext();
  const data = usePaginatedFavorites();

  return (
    <AccountScreenContainer screen="favorites">
      <AccountScreenTitle title={t('account.favorites.title')} />
      <AccountScreenSection>
        <FavoritesScreenContent {...data} />
      </AccountScreenSection>
    </AccountScreenContainer>
  );
};

export const FavoritesScreenContent = ({
  favorites,
  canLoadMore,
  isLoading,
  handleLoadMore,
}: ReturnType<typeof usePaginatedFavorites>) => {
  const { t } = useLocalizationContext();
  const itemsPerRow = useItemsPerRow();

  if (isLoading && favorites.length === 0) return <LoadingState />;

  if (!isLoading && favorites.length === 0) return <EmptyState />;

  return (
    <>
      <HStack itemsPerRow={itemsPerRow}>
        {favorites.map((favorite) => (
          <ConnectedFavorite key={favorite.id} favorite={favorite} />
        ))}
      </HStack>
      {favorites.length > 0 && canLoadMore ? (
        <Button
          testID="account.favorites.load-more"
          palette="secondary"
          accessibilityRole="button"
          accessibilityLabel={t('account.favorites.load-more')}
          accessibilityHint={t('account.favorites.load-more')}
          style={styles.loadMoreButton}
          isLoading={isLoading}
          onPress={handleLoadMore}
        >
          {t('account.favorites.load-more')}
        </Button>
      ) : null}
    </>
  );
};

const ConnectedFavorite = ({
  favorite,
}: {
  readonly favorite: ReturnType<
    typeof usePaginatedFavorites
  >['favorites'][number];
}) => {
  const startReorderLineItem = useReorderLineItem(favorite);

  return (
    <SharedLineItem
      lineItem={favorite as PartialLineItem}
      onAddToBag={startReorderLineItem}
    />
  );
};

const LoadingState = () => {
  const itemsPerRow = useItemsPerRow();

  return (
    <LoadingPlaceholder
      palette="quinoa"
      rows={4}
      columns={itemsPerRow}
      rowHeight={558}
    />
  );
};

const EmptyState = () => {
  const { t } = useLocalizationContext();

  return (
    <View style={styles.emptyContainer}>
      <Image
        source={IllusEmployeeBag}
        style={styles.emptyImage}
        accessibilityIgnoresInvertColors
      />
      <BodyText style={styles.noResultsText}>
        {t('account.favorites.empty')}
      </BodyText>
    </View>
  );
};

type Favorites = FavoritesQuery['favoritedLineItems'];
const usePaginatedFavorites = () => {
  const [canLoadMore, setCanLoadMore] = React.useState(true);
  const [page, setPage] = React.useState(0);
  const [favorites, setFavorites] = React.useState<Favorites>([]);
  const variables = { input: { page } };
  const [response, fetchFavorites] = useFavoritesQuery({
    variables,
    pause: true,
    requestPolicy: 'cache-and-network',
  });
  const isLoading = response.fetching;
  const favoritedLineItems = React.useMemo(
    () => response.data?.favoritedLineItems ?? [],
    [response?.data?.favoritedLineItems],
  );

  useFocusEffect(fetchFavorites);
  React.useEffect(() => {
    const existing = new Set(favorites.map(({ id }) => id));
    const added = favoritedLineItems.filter(({ id }) => !existing.has(id));
    const empty = favorites.length === 0 && favoritedLineItems.length === 0;

    setCanLoadMore(empty || added.length === 10);
    setFavorites(uniqBy([...favorites, ...favoritedLineItems], 'id'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [favoritedLineItems]);
  const handleLoadMore = React.useCallback(() => {
    setPage(Number(page) + 1);
    fetchFavorites();
  }, [page, fetchFavorites, setPage]);

  return { favorites, canLoadMore, isLoading, handleLoadMore };
};

const useItemsPerRow = () => {
  const { match } = useResponsive();

  return match([1, 2]);
};

const styles = StyleSheet.create({
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyImage: {
    width: 220,
    height: 220,
    marginBottom: theme.spacing.medium,
  },
  noResultsText: {
    textAlign: 'center',
  },
  loadMoreButton: {
    minWidth: 200,
    maxWidth: 200,
    marginHorizontal: 'auto',
    marginTop: theme.spacing.large,
  },
});
